import { ApolloError, useQuery } from '@apollo/client';
import { logger } from '@monofront/logging';
import {
  ICheckoutCart,
  ICheckoutNotification,
  MembershipPlan,
} from '@monofront/types';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { GET_CHECKOUT_CART } from '../queries/checkout';
import { useLocalStorage } from '../hooks';
import invariant from 'tiny-invariant';

interface ICheckoutCartActions {
  updateIsLoading: (value: boolean) => void;
  setIsFromCart: (value: boolean) => void;
}

interface ICheckoutCartState {
  checkoutCart?: ICheckoutCart;
  isSameShippingAndBillingAddress: boolean;
  membershipPlan: MembershipPlan;
  setMembershipPlan: Dispatch<SetStateAction<MembershipPlan>>;
  isLoading: boolean;
  error?: ApolloError;
  checkoutNotifications?: [ICheckoutNotification];
  isScrollAvailable: boolean;
  setIsScrollAvailable: Dispatch<SetStateAction<boolean>>;
  useAuthentication: () => { omsId: string | null; isLoading: boolean };
}

interface ICheckoutCartQuery {
  getCheckoutCartWithRefresh: {
    cart: ICheckoutCart;
    notifications: [ICheckoutNotification];
  };
}

interface ICheckoutBeforeNext {
  beforeNextAction: Array<() => void>;
  pushBeforeNextAction: (value: () => void) => void;
}

type ICheckoutContextId = [
  string | undefined,
  (id: string | undefined) => void,
];

const CheckoutContextActions = createContext<ICheckoutCartActions | null>(null);
const CheckoutContextState = createContext<ICheckoutCartState | null>(null);
const CheckoutContextId = createContext<ICheckoutContextId | null>(null);
const CheckoutContextBeforeNextAction =
  createContext<ICheckoutBeforeNext | null>(null);

export const CheckoutCartProvider = ({
  useAuthentication,
  children,
  isMobileApp,
}) => {
  const { omsId } = useAuthentication();
  const [
    checkoutCartIdStorage,
    setCheckoutCartIdStorage,
    { deleteItem: deleteCheckoutCartIdStorage },
  ] = useLocalStorage<string | undefined>('lfCheckoutId', undefined);
  const [checkoutCartIdState, setCheckoutCartIdState] = useState<
    string | undefined
  >(undefined);
  const setCheckoutCartId = useCallback(
    (id: string | undefined) => {
      // Prevent mobile app from saving it to local storage, because we have no way to
      // clear it from native side in Cart screen.
      if (!isMobileApp) {
        setCheckoutCartIdStorage(id);
      } else {
        setCheckoutCartIdState(id);
      }
    },
    [setCheckoutCartIdStorage, setCheckoutCartIdState],
  );

  // The mobile app keeps the checkout id in React state only (see
  // setCheckoutCartId). Ignore the persisted value for the mobile app so that
  // users who still have a stale `lfCheckoutId` in local storage (saved by a
  // previous app version) don't get stuck on an old checkout.
  const checkoutCartId = isMobileApp
    ? checkoutCartIdState
    : checkoutCartIdStorage ?? checkoutCartIdState;

  // One-time cleanup of the stale `lfCheckoutId` for mobile app users. We can't
  // clear it from the native side without shipping a new app build, so we do it
  // here on the web side (the webview runs this web code).
  useEffect(() => {
    if (isMobileApp && checkoutCartIdStorage !== undefined) {
      deleteCheckoutCartIdStorage();
    }
  }, [isMobileApp, checkoutCartIdStorage, deleteCheckoutCartIdStorage]);

  const [isScrollAvailable, setIsScrollAvailable] = useState(false);
  // const [isFromCart, setIsFromCart] = useState<boolean>(false);

  const [membershipPlan, setMembershipPlan] = useState<MembershipPlan>(
    MembershipPlan.YEARLY,
  );
  const [isForceLoading, setIsForceLoading] = useState(!omsId);

  const canFetchCart = Boolean(omsId && checkoutCartId);

  const { data, loading, error } = useQuery<ICheckoutCartQuery>(
    GET_CHECKOUT_CART,
    {
      variables: {
        tokenValue: checkoutCartId,
        refresh: false /*!isFromCart*/,
      },
      skip: !canFetchCart,
      notifyOnNetworkStatusChange: true,
      ssr: false,
    },
  );

  useEffect(() => {
    if (data?.getCheckoutCartWithRefresh.cart.checkoutState === 'completed') {
      // The order is placed and we drop our reference to its cart here, so any
      // screen still mounted on the checkout is left with no cart to render.
      // Logged because that is the state a customer restarts the funnel from —
      // paying a second time for an order that already went through. Faro stamps
      // its session_id on this, and token_value joins it to the lego/OMS logs for
      // the order, which is the correlation we otherwise have no way to make.
      logger.warn('[checkout] cart reference dropped after completion', {
        token_value: checkoutCartId,
      });

      setCheckoutCartId(undefined);
    }
  }, [data]);

  const [beforeNextAction, pushBeforeNextAction] = useState<Array<() => void>>(
    [],
  );

  useEffect(() => {
    setIsForceLoading(false);
  }, [omsId]);

  const checkoutCart: ICheckoutCart | undefined =
    data?.getCheckoutCartWithRefresh?.cart;
  const checkoutNotifications: [ICheckoutNotification] | undefined =
    data?.getCheckoutCartWithRefresh?.notifications;

  const isSameShippingAndBillingAddress = useMemo(() => {
    if (
      checkoutCart &&
      checkoutCart.shippingAddress &&
      checkoutCart.billingAddress &&
      checkoutCart.shippingAddress.hash !== undefined
    ) {
      return (
        checkoutCart.shippingAddress.hash === checkoutCart.billingAddress.hash
      );
    }

    return false;
  }, [checkoutCart?.shippingAddress?.hash, checkoutCart?.billingAddress?.hash]);

  return (
    <CheckoutContextActions.Provider
      value={useMemo(
        () => ({
          updateIsLoading: (value) => setIsForceLoading(value),
          setIsFromCart: () => undefined, // refresh deactivated
        }),
        [],
      )}
    >
      <CheckoutContextState.Provider
        value={useMemo(
          () => ({
            checkoutCart,
            checkoutNotifications,
            isSameShippingAndBillingAddress,
            membershipPlan,
            setMembershipPlan,
            isLoading: loading || isForceLoading,
            error,
            isScrollAvailable,
            setIsScrollAvailable,
            useAuthentication,
          }),
          [
            checkoutCart,
            checkoutNotifications,
            isSameShippingAndBillingAddress,
            loading,
            isForceLoading,
            error,
            isScrollAvailable,
            setIsScrollAvailable,
            useAuthentication,
            membershipPlan,
          ],
        )}
      >
        <CheckoutContextId.Provider
          value={useMemo(
            () => [checkoutCartId, setCheckoutCartId],
            [checkoutCartId],
          )}
        >
          <CheckoutContextBeforeNextAction.Provider
            value={useMemo(
              () => ({
                beforeNextAction,
                pushBeforeNextAction: (val) =>
                  pushBeforeNextAction((prev) => [...prev, val]),
              }),
              [beforeNextAction],
            )}
          >
            {children}
          </CheckoutContextBeforeNextAction.Provider>
        </CheckoutContextId.Provider>
      </CheckoutContextState.Provider>
    </CheckoutContextActions.Provider>
  );
};

export const CheckoutCartProviderId = ({ checkoutCartId, children }) => {
  const [id, setId] = useCheckoutCartId();

  useEffect(() => {
    if (id !== checkoutCartId) {
      setId(checkoutCartId);
    }
  }, [checkoutCartId]);

  return children;
};

export const useCheckoutCartId = () => {
  const ctx = useContext(CheckoutContextId);

  invariant(ctx, 'CheckoutCartProviderId is missing upper in the tree');

  return ctx;
};
export const useCheckoutCartActions = () => {
  const ctx = useContext(CheckoutContextActions);

  invariant(ctx, 'CheckoutContextActions is missing upper in the tree');

  return ctx;
};
export const useCheckoutCartState = () => {
  const ctx = useContext(CheckoutContextState);

  invariant(ctx, 'CheckoutContextState is missing upper in the tree');

  return ctx;
};
export const useCheckoutBeforeNextAction = () => {
  const ctx = useContext(CheckoutContextBeforeNextAction);

  invariant(
    ctx,
    'CheckoutContextBeforeNextAction is missing upper in the tree',
  );

  return ctx;
};
